import { useEffect, useState } from 'react';
import { Copy, KeyRound, Pencil, Plus, Trash2, Webhook as WebhookIcon } from 'lucide-react';
import { WEBHOOK_LIMITS, type Webhook } from '@spawnwire/shared';
import { Button, EmptyState, Field, Input, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { copyText } from '../../lib/hooks';
import { publicOrigin } from '../../lib/platform';
import { ui } from '../../store/ui';
import { Section } from './SettingsLayout';

/** The address a webhook is posted to. Shown once, when the webhook is made or its secret is replaced. */
export function webhookAddress(path: string): string {
  return `${publicOrigin()}${path}`;
}

function SecretUrl({ url, onDone }: { url: string; onDone: () => void }) {
  const full = webhookAddress(url);
  return (
    <div className="rounded-lg border border-accent bg-accent-soft p-3" role="status">
      <p className="text-sm font-medium text-fg">Copy the webhook URL now. It is shown only this once.</p>
      <p className="mt-1 text-xs text-muted">Anyone who has it can post in this channel, so keep it secret. If it leaks, replace it.</p>
      <div className="mt-2 flex gap-2">
        <Input readOnly value={full} aria-label="Webhook URL" onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" />
        <Button variant="secondary" icon={Copy} onClick={() => void copyText(full).then(
              () => ui.toast('Copied.', 'success'),
              () => ui.toast('Could not copy. Select the URL and copy it by hand.', 'error'),
            )
          }>
          Copy
        </Button>
      </div>
      <div className="mt-2 flex justify-end">
        <Button size="sm" variant="ghost" onClick={onDone}>
          I have copied it
        </Button>
      </div>
    </div>
  );
}

function WebhookRow({ hook, spaceId, onChanged, onSecret }: { hook: Webhook; spaceId: string; onChanged: () => void; onSecret: (id: string, url: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(hook.name);
  const [busy, setBusy] = useState<'save' | 'reset' | 'delete' | null>(null);
  const [confirm, setConfirm] = useState<'reset' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const url = `/spaces/${spaceId}/webhooks/${hook.id}`;

  const run = async (kind: 'save' | 'reset' | 'delete', fn: () => Promise<unknown>) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
      setConfirm(null);
      setEditing(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <li className="rounded-lg border border-line bg-sidebar p-3" data-testid="webhook" aria-label={hook.name}>
      {editing ? (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void run('save', async () => {
              await http.patch(url, { name });
              onChanged();
            });
          }}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={WEBHOOK_LIMITS.name} aria-label="Webhook name" autoFocus />
          <Button type="submit" loading={busy === 'save'} disabled={!name.trim() || name.trim() === hook.name}>
            Save
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </form>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <WebhookIcon className="size-4 shrink-0 text-muted" aria-hidden />
            <span className="truncate font-medium text-fg">{hook.name}</span>
          </div>
          {confirm === null && (
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setEditing(true)}>
                Rename
              </Button>
              <Button size="sm" variant="ghost" icon={KeyRound} onClick={() => setConfirm('reset')}>
                New URL
              </Button>
              <Button size="sm" variant="ghost" icon={Trash2} className="text-danger hover:text-danger" onClick={() => setConfirm('delete')}>
                Delete
              </Button>
            </div>
          )}
        </div>
      )}
      {confirm && (
        <div className="mt-2 flex flex-wrap items-center gap-2" role="group" aria-label="Confirm">
          <span className="text-sm text-fg">{confirm === 'reset' ? 'Replace the URL? The old one stops working at once.' : 'Delete this webhook? Its URL stops working.'}</span>
          <Button
            size="sm"
            variant="danger"
            loading={busy === confirm}
            onClick={() =>
              void run(confirm, async () => {
                if (confirm === 'reset') {
                  const fresh = await http.post<Webhook>(`${url}/token`);
                  if (fresh.url) onSecret(hook.id, fresh.url);
                } else {
                  await http.del(url);
                  onChanged();
                }
              })
            }
          >
            {confirm === 'reset' ? 'Yes, replace it' : 'Yes, delete it'}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => setConfirm(null)}>
            Keep
          </Button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}
    </li>
  );
}

/** A channel's incoming webhooks: other services post here through a secret URL. */
export function ChannelWebhooks({ channelId, spaceId }: { channelId: string; spaceId: string }) {
  const [list, setList] = useState<Webhook[] | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The secret of a webhook that was just made or reset, kept only while this screen is open. */
  const [secret, setSecret] = useState<{ id: string; url: string } | null>(null);

  const load = () =>
    http
      .get<{ data: Webhook[] }>(`/channels/${channelId}/webhooks`)
      .then((res) => setList(res.data))
      .catch((err) => setError(errorMessage(err)));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load only depends on the channel
  }, [channelId]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const made = await http.post<Webhook>(`/channels/${channelId}/webhooks`, { name: name.trim() });
      if (made.url) setSecret({ id: made.id, url: made.url });
      setName('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Section description="Webhooks let other services, like GitHub, a monitoring tool or your own script, post messages in this channel. Each has a secret URL; nothing else is needed to use it.">
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void create();
          }}
        >
          <div className="flex-1">
            <Field label="New webhook" htmlFor="wh-name" hint="The name its messages are posted under.">
              <Input id="wh-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={WEBHOOK_LIMITS.name} placeholder="Deploy notices" />
            </Field>
          </div>
          <Button type="submit" icon={Plus} loading={busy} disabled={!name.trim()}>
            Create
          </Button>
        </form>
        {error && (
          <p role="alert" className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}
      </Section>
      <Section title="Webhooks in this channel">
        {secret && <SecretUrl url={secret.url} onDone={() => setSecret(null)} />}
        {list === null ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : list.length === 0 ? (
          <EmptyState icon={WebhookIcon} title="No webhooks yet" body="Create one above to get a URL other services can post to." />
        ) : (
          <ul className="space-y-2">
            {list.map((hook) => (
              <WebhookRow key={hook.id} hook={hook} spaceId={spaceId} onChanged={() => void load()} onSecret={(id, url) => setSecret({ id, url })} />
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
